
import React, { createContext, useContext, useState, useEffect } from 'react';

interface SidebarContextType {
  compact: boolean;
  setCompact: React.Dispatch<React.SetStateAction<boolean>>;
}

const defaultContext: SidebarContextType = {
  compact: false,
  setCompact: () => {},
};

const SidebarContext = createContext<SidebarContextType>(defaultContext);

export function useSidebarContext() {
  return useContext(SidebarContext);
}

interface SidebarProviderProps {
  children: React.ReactNode;
}

// Sanitize stored sidebar state to ensure no unwanted items
function sanitizeSidebarState(state: string): any {
  try {
    const parsedState = JSON.parse(state);
    
    // Remove any properties that might contain 'ai readiness'
    if (parsedState && typeof parsedState === 'object') {
      Object.keys(parsedState).forEach(key => {
        if (
          key.toLowerCase().includes('ai') || 
          key.toLowerCase().includes('readiness') ||
          (typeof parsedState[key] === 'string' && 
           parsedState[key].toLowerCase().includes('ai readiness'))
        ) {
          delete parsedState[key];
        }
      });
    }
    
    return parsedState;
  } catch (e) {
    console.error('Error sanitizing sidebar state:', e);
    return null;
  }
}

export function SidebarProvider({ children }: SidebarProviderProps) {
  // Initialize with default value (not compact)
  const [compact, setCompact] = useState(false);
  
  // On mount, try to load saved preference but sanitize it first
  useEffect(() => {
    try {
      const savedCompactPref = localStorage.getItem('sidebar_compact');
      if (savedCompactPref) {
        setCompact(JSON.parse(savedCompactPref));
      }
      
      // Clear any sidebar state that might contain AI Readiness references
      const sidebarState = localStorage.getItem('sidebar_state');
      if (sidebarState) {
        if (sidebarState.toLowerCase().includes('ai') || 
            sidebarState.toLowerCase().includes('readiness')) {
          localStorage.removeItem('sidebar_state');
        } else {
          // Sanitize and save back the cleaned state
          const sanitizedState = sanitizeSidebarState(sidebarState);
          if (sanitizedState) {
            localStorage.setItem('sidebar_state', JSON.stringify(sanitizedState));
          }
        }
      }
    } catch (e) {
      console.error('Error loading sidebar preferences:', e);
    }
  }, []);
  
  // Save compact preference when it changes
  useEffect(() => {
    try {
      localStorage.setItem('sidebar_compact', JSON.stringify(compact));
    } catch (e) {
      console.error('Error saving sidebar preferences:', e);
    }
  }, [compact]);
  
  return (
    <SidebarContext.Provider value={{ compact, setCompact }}>
      {children}
    </SidebarContext.Provider>
  );
}
